<template>
  <div class="admin-logs-container">
    <Loading :show="loading" />

    <div :class="{ disabled: loading }">
      <h1>Ändringsloggar</h1>
      <hr />

      <!-- Filters Section -->
      <div class="logs-filters">
        <div class="filter-group">
          <label for="filter-user">Användare</label>
          <select id="filter-user" v-model="filterUser" @change="handleFilterChange">
            <option value="">Alla användare</option>
            <option v-for="user in distinctEmails" :key="user" :value="user">
              {{ user }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label for="filter-action">Händelse</label>
          <select id="filter-action" v-model="filterAction" @change="handleFilterChange">
            <option value="">Alla händelser</option>
            <option v-for="action in distinctActions" :key="action" :value="action">
              {{ formatAction(action) }}
            </option>
          </select>
        </div>

        <div class="filter-group text-search">
          <label for="filter-search">Fritextsök (detaljer)</label>
          <input
            id="filter-search"
            type="text"
            v-model="searchText"
            placeholder="Sök i detaljer..."
            @input="debounceSearch"
          />
        </div>
      </div>

      <!-- List of change logs -->
      <div class="logs-list-section">
        <table class="logs-table">
          <thead>
            <tr>
              <th>Tid</th>
              <th>Användare</th>
              <th>Händelse</th>
              <th>Detaljer</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in logs" :key="log.id">
              <td class="time-cell">{{ formatDate(log.created_at) }}</td>
              <td class="email-cell">{{ log.email }}</td>
              <td>
                <span :class="'action-badge ' + log.action">
                  {{ formatAction(log.action) }}
                </span>
              </td>
              <td class="details-cell">
                <div v-html="formatDetails(log.action, log.details)"></div>
              </td>
              <td class="ip-cell">{{ log.ip_address || '-' }}</td>
            </tr>
            <tr v-if="logs.length === 0">
              <td colspan="5" class="empty-cell">Inga loggar hittades.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-section" v-if="lastPage > 1">
        <button
          class="btn-nav"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        >
          Föregående
        </button>
        <span class="pagination-info">
          Sida {{ currentPage }} av {{ lastPage }} (totalt {{ totalLogs }} rader)
        </span>
        <button
          class="btn-nav"
          :disabled="currentPage === lastPage"
          @click="changePage(currentPage + 1)"
        >
          Nästa
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/components/Loading';
import API from '@/classes/API';
import moment from 'moment';

const textMap = {
  accomodationaddon: {
    text1: 'Rubrik',
    text2: 'Bild',
    text3: 'Beskrivning',
    text6: 'Plural',
    text4: 'Kopplad produkt',
    text7: 'Obl. städ',
  },
  pagetexts: {
    text1: 'Slug',
    text2: 'Text',
  },
  skilettingproduct: {
    text1: 'Rubrik',
    text2: 'Bild',
    text3: 'Underrubrik',
    text4: 'Beskrivning',
    text7: 'Underrubrik ny skidhyra',
    number1: 'Grupperas med pool',
    number2: 'Ordning på grupp',
    number3: 'Ordning i grupp',
    text5: 'Bild (m. hjälm)',
    text6: 'Kortare rubrik',
    json1: {
      label: 'Längdkategorier',
      format: 'array',
    },
  },
  skischoolgroup: {
    text1: {
      label: 'Rubrik',
      format: 'json',
    },
    text2: {
      label: 'Text',
      format: 'json',
    },
    text3: {
      label: 'Förkunskaper',
      format: 'json',
    },
    text4: {
      label: 'Bildrubrik',
      format: 'json',
    },
    text5: {
      label: 'Önskemål',
      format: 'json',
    },
  },
};

export default {
  components: {
    Loading,
  },
  data() {
    return {
      loading: false,
      logs: [],
      distinctEmails: [],
      distinctActions: [],
      filterUser: '',
      filterAction: '',
      searchText: '',
      currentPage: 1,
      lastPage: 1,
      totalLogs: 0,
      searchTimeout: null,
    };
  },
  mounted() {
    this.fetchLogs();
  },
  methods: {
    formatDate(date) {
      if (!date) {
        return '-';
      }
      return moment(date).format('YYYY-MM-DD HH:mm:ss');
    },
    formatAction(action) {
      if (action === 'update_setting') {
        return 'Inställning';
      }
      if (action === 'run_sync_command') {
        return 'Synk';
      }
      if (action === 'update_pool') {
        return 'Område';
      }
      if (action === 'update_unit') {
        return 'Boende';
      }
      if (action === 'create_text') {
        return 'Skapat text';
      }
      if (action === 'update_text') {
        return 'Text';
      }
      if (action === 'pull_report') {
        return 'Rapport';
      }
      if (action === 'login') {
        return 'Inloggning';
      }
      if (action === 'logout') {
        return 'Utloggning';
      }
      if (action === 'create_admin_user') {
        return 'Skapat admin';
      }
      if (action === 'delete_admin_user') {
        return 'Tagit bort admin';
      }
      if (action === 'token_create') {
        return 'Token skapad';
      }
      if (action === 'token_deactivate') {
        return 'Token inaktiverad';
      }
      if (action === 'token_new_ip') {
        return 'Ny Token-IP';
      }
      return action;
    },
    formatCategory(category) {
      if (category === 'accomodationaddon') {
        return 'Tillval';
      }
      if (category === 'skilettingproduct') {
        return 'Skidhyra';
      }
      if (category === 'pagetexts') {
        return 'Sidor';
      }
      if (category === 'skischoolgroup') {
        return 'Skidskola';
      }
      return category;
    },
    formatPropertyLabel(category, prop) {
      if (textMap[category] && textMap[category][prop]) {
        if (typeof textMap[category][prop] === 'string') {
          return textMap[category][prop];
        }
        if (textMap[category][prop].label) {
          return textMap[category][prop].label;
        }
      }
      return prop;
    },
    formatPropertyDiff(category, prop, value) {
      if (textMap[category] && textMap[category][prop] && textMap[category][prop].format) {
        if (textMap[category][prop].format === 'boolean') {
          return !value || value === '0' ? 'Nej' : 'Ja';
        } else if (textMap[category][prop].format === 'array') {
          // Check if array
          if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : '';
          } else if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              if (Array.isArray(parsed)) {
                return parsed.length > 0 ? parsed.join(', ') : '';
              }
            } catch (e) {
              // Not a valid JSON, return original string
              return value;
            }
          }
        } else if (textMap[category][prop].format === 'json') {
          // Check if is a object
          if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              if (typeof parsed === 'object' && parsed !== null) {
                return Object.entries(parsed).map(([key, val]) => `${key}: ${val}`).join(', ');
              }
            } catch (e) {
              // Not a valid JSON, return original string
              return value;
            }
          } else if (typeof value === 'object' && value !== null) {
            return Object.entries(value).map(([key, val]) => `${key}: ${val}`).join(', ');
          }
        }
      }

      // String value
      return value;
    },
    formatDiffSentence(category, prop, was, is) {
      if (was === is) {
        return '';
      }

      const formattedDiffWas = this.formatPropertyDiff(category, prop, was);
      const formattedDiffIs = this.formatPropertyDiff(category, prop, is);
      const formattedLabel = this.formatPropertyLabel(category, prop);

      if (formattedDiffWas === formattedDiffIs) {
        return '';
      }

      if (formattedDiffWas || formattedDiffIs) {
        return `<strong>${formattedLabel}</strong> ändrades <s><strong>${formattedDiffWas || was || 'ej ifyllt'}</strong></s> till <strong>${formattedDiffIs || is || 'ej ifyllt'}</strong>.`;
      }

      return `<strong>${formattedLabel}</strong> ändrades <s><strong>${was || 'ej ifyllt'}</strong></s> till <strong>${is || 'ej ifyllt'}</strong>.`;
    },
    formatDetails(action, detailsString) {
      if (!detailsString) {
        return '-';
      }

      try {
        const details = JSON.parse(detailsString);
        const category = details.category ? this.formatCategory(details.category) : '';

        if (action === 'update_setting') {
          return `Ändrade parametern <code>${details.key}</code> från <strong>"${details.value_was || ''}"</strong> till <strong>"${details.value_is || ''}"</strong>.`;
        }
        if (action === 'run_sync_command') {
          const args = details.args && Object.keys(details.args).length > 0
            ? JSON.stringify(details.args)
            : 'inga';
          return `Triggar synk-kommandot <code>${details.command}</code> med argument <code>${args}</code>.`;
        }
        if (action === 'update_pool') {
          return `Uppdaterade området <strong>"${details.name}"</strong> (visningsnamn ändrat från <strong>"${details.display_name_was || ''}"</strong> till <strong>"${details.display_name_is || ''}"</strong>).`;
        }
        if (action === 'update_unit') {
          return `Uppdaterade boende <strong>"${details.name}"</strong> (ID: ${details.id}) (visningsnamn ändrat från <strong>"${details.display_name_was || ''}"</strong> till <strong>"${details.display_name_is || ''}"</strong>).`;
        }
        if (action === 'create_text') {
          return `Skapade ny text i <strong>"${category}"</strong> (Pool: <code>${details.id1_is || details.id1 || details.slug || '-'}</code>).`;
        }
        if (action === 'update_text') {
          let changeMsg = `Ändrade text i kategorin <strong>"${category}"</strong> (Pool: <code>${details.id1_is || details.id1 || details.slug || '-'}</code>).`;

          const checkProperties = ['text1', 'text2', 'text3', 'text4', 'text5', 'text6', 'text7', 'number1', 'number2', 'number3', 'json1'];

          checkProperties.forEach((prop) => {
            const wasProp = `${prop}_was`;
            const isProp = `${prop}_is`;
            if (details[wasProp] !== details[isProp]) {
              changeMsg += ` ${this.formatDiffSentence(details.category, prop, details[wasProp], details[isProp])}`;
            }
          });

          return changeMsg;
        }
        if (action === 'pull_report') {
          if (details.type === 'emails') {
            return `Exporterade e-postadresser för perioden <strong>${details.start || ''}</strong> till <strong>${details.end || ''}</strong>.`;
          }
          if (details.type === 'emails-extended') {
            return `Exporterade utökade e-postadresser med pool för perioden <strong>${details.start || ''}</strong> till <strong>${details.end || ''}</strong>.`;
          }
          if (details.type === 'codes_with_extra_info') {
            return `Exporterade bokningskoder med extrafält för perioden <strong>${details.start || ''}</strong> till <strong>${details.end || ''}</strong>.`;
          }
          if (details.type === 'excel') {
            return `Exporterade orderlista till Excel för perioden <strong>${details.start || ''}</strong> till <strong>${details.end || ''}</strong>.`;
          }
          if (details.type === 'sales_report') {
            return `Drog ut försäljningsrapport (Filter: <code>${details.only || 'alla'}</code>, Gruppering: <code>${details.group || 'datum'}</code>).`;
          }
          if (details.type === 'translations_excel') {
            return `Exporterade översättningsfiler till Excel (Språk: <strong>${details.lang || ''}</strong>).`;
          }
          return `Hämtade en rapport (${details.type || ''}).`;
        }
        if (action === 'login') {
          const methodText = details.method === 'microsoft_sso' ? 'Microsoft SSO' : (details.method || 'SSO');
          return `Loggade in via <strong>${methodText}</strong>. <span class="ua-text" title="${details.user_agent || ''}">(${details.user_agent || 'Okänd webbläsare'})</span>`;
        }
        if (action === 'logout') {
          return `Loggade ut. <span class="ua-text" title="${details.user_agent || ''}">(${details.user_agent || 'Okänd webbläsare'})</span>`;
        }
        if (action === 'create_admin_user') {
          return `Skapade administratörsanvändare <strong>${details.email || ''}</strong> med rollen <strong>${details.role || ''}</strong>.`;
        }
        if (action === 'delete_admin_user') {
          return `Tog bort administratörsanvändare <strong>${details.email || ''}</strong> (roll: <strong>${details.role || ''}</strong>).`;
        }
        if (action === 'token_create') {
          const expiry = details.expiry_date ? moment(details.expiry_date).format('YYYY-MM-DD HH:mm') : '-';
          return `Skapade ny endpoint-token <strong>"${details.name || ''}"</strong> (giltig till: <strong>${expiry}</strong>).`;
        }
        if (action === 'token_deactivate') {
          return `Inaktiverade endpoint-token <strong>"${details.name || ''}"</strong>.`;
        }
        if (action === 'token_new_ip') {
          return `Ny IP-adress <code>${details.ip || ''}</code> anropade endpoint <code>${details.endpoint || ''}</code> med token <strong>"${details.token_name || ''}"</strong>.`;
        }
        return detailsString;
      } catch (e) {
        return detailsString;
      }
    },
    async fetchLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          email: this.filterUser,
          action: this.filterAction,
          search: this.searchText,
        };

        const response = await API.get('/admin/logs', params);
        if (response && response.success) {
          this.logs = response.logs || [];
          this.currentPage = response.current_page || 1;
          this.lastPage = response.last_page || 1;
          this.totalLogs = response.total || 0;
          this.distinctEmails = response.emails || [];
          this.distinctActions = response.actions || [];
        } else {
          this.$toast.error((response && response.errorMessage) || 'Det gick inte att hämta loggar.', 'Fel', {
            position: 'topRight',
          });
        }
      } catch (error) {
        this.$toast.error('Ett oväntat fel uppstod.', 'Fel', {
          position: 'topRight',
        });
      } finally {
        this.loading = false;
      }
    },
    handleFilterChange() {
      this.currentPage = 1;
      this.fetchLogs();
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchLogs();
      }, 300);
    },
    changePage(page) {
      if (page >= 1 && page <= this.lastPage) {
        this.currentPage = page;
        this.fetchLogs();
      }
    },
  },
};
</script>

<style scoped lang="stylus">
@import '~@/global.styl'

.admin-logs-container
  admin-container()
  max-width 1200px
  margin 0 auto
  padding 20px

.disabled
  opacity 0.5
  pointer-events none
  filter grayscale(100%)

.logs-filters
  display flex
  gap 15px
  flex-wrap wrap
  background-color: rgba(182, 215, 225, .25);
  padding 15px

.filter-group
  display flex
  flex-direction column
  flex 1
  min-width 200px
  label
    font-size 0.85em
    font-weight bold
    margin-bottom 6px
    color #666
  select, input
    padding 8px 10px
    border 1px solid #ccc
    border-radius 4px
    background #fff
    font-size 0.9em
    outline none
    &:focus
      border-color #0066cc

.text-search
  flex 1.5

.logs-table
  width 100%
  border-collapse collapse
  margin-top 15px

th, td
  text-align left
  padding 12px 14px
  border-bottom 1px solid #ddd

th
  font-weight bold
  background #f1f1f1

.time-cell
  font-size 0.9em
  color #555
  white-space nowrap

.email-cell
  font-weight 500
  color #333
  max-width 180px
  overflow hidden
  text-overflow ellipsis
  white-space nowrap

.action-badge
  padding 4px 8px
  border-radius 12px
  font-size 0.85em
  font-weight bold
  text-transform uppercase
  display inline-block
  &.update_setting
    background #fff3cd
    color #856404
  &.run_sync_command
    background #d1ecf1
    color #0c5460
  &.update_pool
    background #e2f0fe
    color #0066cc
  &.update_unit
    background #e2f0fe
    color #0066cc
  &.create_text
    background #d4edda
    color #155724
  &.update_text
    background #d4edda
    color #155724
  &.pull_report
    background #e2f0fe
    color #0066cc
  &.login
    background #d4edda
    color #155724
  &.logout
    background #f8d7da
    color #721c24
  &.create_admin_user
    background #d4edda
    color #155724
  &.delete_admin_user
    background #f8d7da
    color #721c24
  &.token_create
    background #d4edda
    color #155724
  &.token_deactivate
    background #f8d7da
    color #721c24
  &.token_new_ip
    background #e2f0fe
    color #0066cc

.ua-text
  font-size 0.8em
  color #888
  display inline-block
  margin-left 4px

.details-cell
  font-size 0.95em
  line-height 1.4
  word-break break-word
  code
    background-color #f1f1f1
    padding 2px 5px
    border-radius 3px
    font-family monospace

.ip-cell
  font-size 0.85em
  color #777

.empty-cell
  text-align center
  padding 30px
  color #777

.pagination-section
  display flex
  align-items center
  justify-content center
  gap 15px
  margin-top 20px
  padding 15px 0
  border-top 1px solid #eee

.btn-nav
  padding 8px 16px
  border 1px solid #ccc
  background #fff
  border-radius 4px
  cursor pointer
  font-weight 500
  transition all 0.2s
  &:disabled
    opacity 0.5
    cursor not-allowed
  &:not(:disabled):hover
    background #f0f0f0
    border-color #999

.pagination-info
  font-size 0.9em
  color #666
</style>



// WEBPACK FOOTER //
// Logs.vue?7d22edc4